import React from 'react';

import { useUserWorkspacesQuery } from '@data/queries/user-workspaces.query';
import { Sidebar } from 'react-components/src/ui/layout/dynamic-layout-with-sidebar';
import Avatar from 'react-components/src/components/avatar';
import { useUserWorkspace } from '@hooks/use-user-workspace';

import Root from './workspace-switcher-root';
import Trigger from './workspace-switcher-trigger';
import Content from './workspace-switcher-content';

type Props = React.PropsWithChildren;

const DEFAULT_LOGO = 'https://imagedelivery.net/srz7f0QqFI2B_1dARS7nLA/98eaadf0-ea0b-4176-f28a-ae7f74f99100/public';

const WorkspaceSwitcher = ({ children }: Props) => {
  const workspaces = useUserWorkspacesQuery();
  const [isDropdownOpen, setIsDropdownOpen] = React.useState<boolean>(false);
  const [userWorkspace] = useUserWorkspace();

  const selectedWorkspace = React.useMemo(
    () => workspaces.find((workspace) => userWorkspace === workspace.apiID),
    [workspaces, userWorkspace]
  );
  const logo = React.useMemo(() => {
    const firstWorkspace = selectedWorkspace || workspaces[0];
    const employerLogo = firstWorkspace?.employment?.employer?.logo;
    if (employerLogo === DEFAULT_LOGO) {
      return null;
    }
    return employerLogo;
  }, [workspaces, selectedWorkspace]);

  if (!selectedWorkspace || (workspaces.length || 0) <= 1) {
    return logo ? (
      <Sidebar.Logo>
        <Avatar name={workspaces[0]?.employment?.employer?.name || 'HSA'} src={logo} size="none" className="w-8 h-8" />
      </Sidebar.Logo>
    ) : (
      <>{children || null}</>
    );
  }

  return (
    <Root isDropdownOpen={isDropdownOpen} setIsDropdownOpen={setIsDropdownOpen}>
      <Trigger selectedWorkspace={selectedWorkspace} />
      <Content setIsDropdownOpen={setIsDropdownOpen} selectedWorkspace={selectedWorkspace} />
    </Root>
  );
};

const Wrapper = ({ ...props }: Props) => (
  <React.Suspense fallback={<div className="w-10 h-10 rounded-xl bg-indigo-90 animate-pulse" />}>
    <WorkspaceSwitcher {...props} />
  </React.Suspense>
);

export { Trigger, Content, Root };

export { default as Dropdown } from './workspace-selection-dropdown';

export default Wrapper;
